import { useState, useEffect } from 'react';
import { Navigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { useAuth } from '@/hooks/useAuth';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import { Shield, Users, Database, Activity, Package, Settings, MessageSquare } from 'lucide-react';
import { UserManagement } from '@/components/admin/UserManagement';
import { PlanManager } from '@/components/admin/PlanManager';
import { SubscriptionFixer } from '@/components/admin/SubscriptionFixer';
import { FeedbackManagement } from '@/components/admin/FeedbackManagement';

interface Profile {
  id: string;
  user_id: string;
  full_name: string;
  email: string;
  created_at: string;
  roles: string[];
  referrer_url?: string;
  utm_source?: string;
  utm_medium?: string;
  utm_campaign?: string;
  utm_content?: string;
  utm_term?: string;
  ref_param?: string;
  landing_page?: string;
  first_visit_at?: string;
  // Onboarding fields
  onboarding_completed?: boolean;
  use_case?: string;
  demo_watched?: boolean;
  selected_plan?: string;
  onboarding_completed_at?: string;
  subscription?: {
    credits_used: number;
    credits_remaining: number;
    status: string;
    billing_cycle: string;
    expires_at: string;
    plan_id: string;
    plans?: {
      name: string;
      credits: number;
    };
  };
}

const Admin = () => {
  const { user, loading: authLoading } = useAuth();
  const [isAdmin, setIsAdmin] = useState(false);
  const [loading, setLoading] = useState(true);
  const [profiles, setProfiles] = useState<Profile[]>([]);
  const [stats, setStats] = useState({
    totalUsers: 0,
    totalProfiles: 0,
    adminCount: 0,
  });

  useEffect(() => {
    if (user) {
      checkAdminStatus();
    }
  }, [user]);

  const checkAdminStatus = async () => {
    try {
      const { data, error } = await supabase
        .from('user_roles')
        .select('role')
        .eq('user_id', user?.id)
        .eq('role', 'admin');

      if (error) throw error;
      
      const hasAdminRole = data && data.length > 0;
      setIsAdmin(hasAdminRole);
      
      if (hasAdminRole) {
        await fetchAdminData();
      }
    } catch (error) {
      console.error('Error checking admin status:', error);
      toast.error('Failed to verify admin status');
    } finally {
      setLoading(false);
    }
  };

  const fetchAdminData = async () => {
    try {
      // Fetch ALL profiles using pagination (Supabase has 1000 row limit per query)
      let allProfiles: any[] = [];
      let page = 0;
      const pageSize = 1000;
      
      while (true) {
        const { data: profilesData, error: profilesError } = await supabase
          .from('profiles')
          .select('*')
          .range(page * pageSize, (page + 1) * pageSize - 1);

        if (profilesError) throw profilesError;
        if (!profilesData || profilesData.length === 0) break;
        
        allProfiles = [...allProfiles, ...profilesData];
        
        if (profilesData.length < pageSize) break; // Last page
        page++;
      }

      // Fetch ALL user roles using pagination
      let allRoles: any[] = [];
      page = 0;
      
      while (true) {
        const { data: rolesData, error: rolesError } = await supabase
          .from('user_roles')
          .select('user_id, role')
          .range(page * pageSize, (page + 1) * pageSize - 1);

        if (rolesError) throw rolesError;
        if (!rolesData || rolesData.length === 0) break;
        
        allRoles = [...allRoles, ...rolesData];
        
        if (rolesData.length < pageSize) break;
        page++;
      }

      // Fetch ALL subscriptions using pagination
      let allSubscriptions: any[] = [];
      page = 0;
      
      while (true) {
        const { data: subscriptionsData, error: subscriptionsError } = await supabase
          .from('user_subscriptions')
          .select(`
            user_id,
            credits_used,
            credits_remaining,
            status,
            billing_cycle,
            expires_at,
            plan_id,
            plans (name, credits)
          `)
          .range(page * pageSize, (page + 1) * pageSize - 1);

        if (subscriptionsError) throw subscriptionsError;
        if (!subscriptionsData || subscriptionsData.length === 0) break;
        
        allSubscriptions = [...allSubscriptions, ...subscriptionsData];
        
        if (subscriptionsData.length < pageSize) break;
        page++;
      }

      // Create lookup maps
      const rolesMap = allRoles.reduce((acc, role) => {
        if (!acc[role.user_id]) acc[role.user_id] = [];
        acc[role.user_id].push(role.role);
        return acc;
      }, {} as Record<string, string[]>);

      const subscriptionsMap = allSubscriptions.reduce((acc, sub) => {
        acc[sub.user_id] = sub;
        return acc;
      }, {} as Record<string, any>);

      // Transform data
      const transformedProfiles = allProfiles.map(profile => ({
        ...profile,
        roles: rolesMap[profile.user_id] || [],
        subscription: subscriptionsMap[profile.user_id] || null
      }));

      setProfiles(transformedProfiles);

      // Calculate stats
      const totalProfiles = transformedProfiles.length;
      const adminCount = transformedProfiles.filter(p => 
        p.roles.includes('admin')
      ).length;

      setStats({
        totalUsers: totalProfiles,
        totalProfiles,
        adminCount,
      });
    } catch (error) {
      console.error('Error fetching admin data:', error);
      toast.error('Failed to load admin data');
    }
  };

  if (authLoading || loading) {
    return (
      <div className="min-h-screen bg-bg flex items-center justify-center">
        <div className="animate-pulse">Loading...</div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/auth" replace />;
  }

  if (!isAdmin) {
    return (
      <div className="min-h-screen bg-bg flex items-center justify-center">
        <Card className="w-full max-w-md">
          <CardHeader>
            <CardTitle className="text-center text-destructive">Access Denied</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-center text-muted-foreground">
              You don't have admin privileges to access this page.
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-bg">
      <header className="bg-card border-b border-border shadow-card">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-primary rounded-full flex items-center justify-center">
              <Shield className="w-5 h-5 text-primary-foreground" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-primary">Admin Dashboard</h1>
              <p className="text-sm text-muted-foreground">
                System administration and user management
              </p>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Stats Cards */}
        <div className="grid gap-4 md:grid-cols-3 mb-8">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Total Users</CardTitle>
              <Users className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{stats.totalUsers}</div>
            </CardContent>
          </Card>
          
          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Profiles</CardTitle>
              <Database className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{stats.totalProfiles}</div>
            </CardContent>
          </Card>
          
          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Administrators</CardTitle>
              <Shield className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{stats.adminCount}</div>
            </CardContent>
          </Card>
        </div>

        {/* Admin Tabs */}
        <Tabs defaultValue="users" className="space-y-6">
          <TabsList className="grid w-full grid-cols-4">
            <TabsTrigger value="users" className="flex items-center gap-2">
              <Users className="w-4 h-4" />
              Users
            </TabsTrigger>
            <TabsTrigger value="plans" className="flex items-center gap-2">
              <Package className="w-4 h-4" />
              Plans
            </TabsTrigger>
            <TabsTrigger value="feedback" className="flex items-center gap-2">
              <MessageSquare className="w-4 h-4" />
              Feedback
            </TabsTrigger>
            <TabsTrigger value="settings" className="flex items-center gap-2">
              <Settings className="w-4 h-4" />
              Settings
            </TabsTrigger>
          </TabsList>

          <TabsContent value="users">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Activity className="w-5 h-5" />
                  User Management
                </CardTitle>
              </CardHeader>
              <CardContent>
                <UserManagement profiles={profiles} onRefresh={fetchAdminData} />
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="plans">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Package className="w-5 h-5" />
                  Subscription Plans
                </CardTitle>
              </CardHeader>
              <CardContent>
                <PlanManager />
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="feedback">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <MessageSquare className="w-5 h-5" />
                  User Feedback
                </CardTitle>
              </CardHeader>
              <CardContent>
                <FeedbackManagement />
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="settings">
            <div className="space-y-6">
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Settings className="w-5 h-5" />
                    Subscription Fixer
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <SubscriptionFixer />
                </CardContent>
              </Card>
              
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Settings className="w-5 h-5" />
                    System Settings
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="text-center py-8">
                    <Settings className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
                    <h3 className="text-lg font-semibold mb-2">System Settings</h3>
                    <p className="text-muted-foreground">
                      Advanced system configuration options will be available here.
                    </p>
                  </div>
                </CardContent>
              </Card>
            </div>
          </TabsContent>
        </Tabs>
      </main>
    </div>
  );
};

export default Admin;